import React, { useEffect, useRef, useState } from "react";

const Reveal: React.FC<{
  children: React.ReactNode;
  variant?: "fadeUp" | "fadeLeft" | "fadeRight" | "scale";
  delayMs?: number;
  className?: string;
}> = ({ children, variant = "fadeUp", delayMs = 0, className = "" }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25, rootMargin: "0px 0px -20% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const base = "transition-all duration-700 ease-out will-change-transform will-change-opacity";
  const delay = delayMs ? ({ transitionDelay: `${delayMs}ms` } as React.CSSProperties) : undefined;
  const hidden: Record<string, string> = {
    fadeUp: "opacity-0 translate-y-6",
    fadeLeft: "opacity-0 -translate-x-6",
    fadeRight: "opacity-0 translate-x-6",
    scale: "opacity-0 scale-95",
  };

  return (
    <div
      ref={ref}
      style={delay}
      className={`${className} ${base} ${shown ? "opacity-100 translate-y-0 translate-x-0 scale-100" : hidden[variant]}`}
    >
      {children}
    </div>
  );
};

/* =======================
   MAIN COMPONENT
   ======================= */
const GabbroIntro: React.FC<{ content?: any }> = ({ content }) => {
  const title = content?.title || "Hamdan International for Gabbro";
  const desc = content?.desc || "Hamdan International for Gabbro – Qatar is a company of Hamdan Holding Group. The group also produces gabbro materials in the Sultanate of Oman since 2008 through Dar Bani Omar Trading & Contracting LLC (DBO).\n\nDar Bani Omar Trading & Contracting LLC (DBO) is a well-established crusher company with high-quality gabbro deposits and three-stage crusher plants. The quarry is located at Al Rissah, Liwa Wilayat Mountain, North Batinah, approximately 36 km from Sohar Port.\n\nThe quarry produces high-density gabbro material with available reserves of approximately 29 million metric tons (13,286,517.82 m³ at 80% recovery), covering an area of about 567,584 square meters.";
  const image = content?.image || "/gabbro/gabbro-stones.jpg";
  const kicker = content?.intro_kicker || "Our Group • Qatar";
  const noteTitle = content?.intro_note_title || "Quick Note";
  const noteDesc = content?.intro_note_desc || "Supplier of crushed quarry materials (gabbro) for concrete and road construction projects in Qatar and the region.";
  const imageLabel = content?.intro_image_label || "High-density Gabbro Aggregates";

  const points = [
    content?.point_1 || "Since 2008 (Group operations)",
    content?.point_2 || "~29 million metric tons reserves",
    content?.point_3 || "~36 km from Sohar Port"
  ];

  const groupCompanies = [
    content?.group_company_1 || "Hamdan Holding Group",
    content?.group_company_2 || "Hamdan Mining International LLC",
    content?.group_company_3 || "Dar Bani Omar Trading & Contracting LLC"
  ];

  const brandDesc = content?.brand_desc || "Strategic supply of crushed aggregates for infrastructure, concrete and road construction works.";
  const brandTags = [
    content?.brand_tag_1 || "Quarry Materials",
    content?.brand_tag_2 || "Reliable Supply",
    content?.brand_tag_3 || "HSE Focus"
  ];

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* TOP ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT */}
          <Reveal variant="fadeLeft" className="lg:col-span-3">
            <div className="sticky top-6">
              <p className="text-xs uppercase tracking-widest text-gray-500">
                {kicker}
              </p>

              <h2 className="mt-3 text-3xl font-semibold leading-tight text-gray-900">
                {title.includes(' for ') ? (
                  <>
                    {title.split(' for ')[0]}
                    <span className="block text-5xl font-light text-gray-900 mt-2">
                      for {title.split(' for ')[1]}
                    </span>
                  </>
                ) : title}
              </h2>

              <div className="mt-6 space-y-2">
                {points.map((p, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full border bg-gray-50"
                  >
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    {p}
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border p-4 bg-gray-50">
                <div className="text-xs uppercase tracking-wider text-gray-500">
                  {noteTitle}
                </div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                  {noteDesc}
                </p>
              </div>
            </div>
          </Reveal>

          {/* MIDDLE IMAGE */}
          <Reveal variant="scale" delayMs={120} className="lg:col-span-6">
            <div className="relative rounded-[28px] overflow-hidden border shadow-sm">
              <img
                src={image}
                alt={title}
                className="w-full h-[460px] object-contain"
              />

              <div className="absolute left-5 top-5 bg-white/90 backdrop-blur px-4 py-3 rounded-2xl border shadow-sm">
                <div className="text-xs uppercase tracking-widest text-gray-600">
                  Introduction
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {imageLabel}
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-yellow-500 via-yellow-400 to-red-500" />
            </div>
          </Reveal>

          {/* RIGHT */}
          <Reveal variant="fadeRight" delayMs={200} className="lg:col-span-3">
            <div className="rounded-2xl border bg-gray-50 p-6">
              <h3 className="text-lg font-bold text-gray-900">INTRODUCTION</h3>

              <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed text-justify">
                <div dangerouslySetInnerHTML={{ __html: (content?.desc || desc).replace(/\n/g, '<br/>') }} />
              </div>

              <div className="mt-5 pt-4 border-t text-xs text-gray-500">
                *Details based on internal company profile.
              </div>
            </div>
          </Reveal>
        </div>

        {/* BOTTOM ROW */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* GROUP COMPANIES */}
          <Reveal variant="fadeUp" delayMs={0} className="md:col-span-2">
            <div className="rounded-2xl border bg-white p-6 shadow-sm h-full">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-lg font-bold text-gray-900">
                  Group Companies
                </h4>
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Our Network
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {groupCompanies.map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* BRAND SUMMARY */}
          <Reveal variant="fadeUp" delayMs={150}>
            <div className="rounded-2xl shadow-sm border h-full bg-white">
              <div className="flex h-full min-h-[220px] flex-col items-center justify-center p-6 text-center">
                <p className="max-w-md text-sm text-gray-700 leading-relaxed">
                  {brandDesc}
                </p>

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {brandTags.map(
                    (tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 rounded-full border bg-gray-50"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default GabbroIntro;
